"use client";

import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";

import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { SanityDocument } from "next-sanity";
import Image from "next/image";
import { urlFor } from "@/sanity/lib/image";
import { useRouter } from "next/navigation";
import { animatePageOut } from "@/lib/pageAnimate";
import { CarouselActionButton } from "../home/services-list/services-carousel-buttons";
import { Card } from "./card";

export interface Gallery4Item {
  id: string;
  title: string;
  description: string;
  href: string;
  image: string;
}

export interface Gallery4Props {
  title?: string;
  description?: string;
  items: SanityDocument[];
  pathname?: string;
}

const Gallery4 = ({ title, description, items, pathname }: Gallery4Props) => {
  const [carouselAPI, setCarouselAPI] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const router = useRouter();

  useEffect(() => {
    if (!carouselAPI) {
      return;
    }

    setCurrent(carouselAPI?.selectedScrollSnap());

    carouselAPI.on("select", () => {
      setCurrent(carouselAPI.selectedScrollSnap());
    });
  }, [carouselAPI]);

  const handleClick = (href: string) => {
    const url = `${pathname}/${href}`;
    animatePageOut(url, router);
  };

  return (
    <section className="w-full font-Satoshi-Medium">
      <div className="container mx-auto max-w-7xl">
        <div className="mb-8 flex items-end justify-between md:mb-10 lg:mb-12">
          <div className="flex flex-col gap-4">
            <h2 className="text-lg font-bold md:text-2xl lg:text-3xl">
              {title}
            </h2>
            <p className="max-w-lg text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col">
        <Carousel
          setApi={setCarouselAPI}
          opts={{
            align: "center",
          }}
        >
          <CarouselContent className="ml-0 2xl:ml-[max(8rem,calc(50vw-700px))] 2xl:mr-[max(0rem,calc(50vw-700px))]">
            {items.map((item) => (
              <CarouselItem
                key={item._id}
                className="max-w-[320px] cursor-pointer pl-[20px] lg:max-w-[400px]"
                onClick={() => handleClick(item.slug.current)}
              >
                <Card className="group relative h-full min-h-[27rem] max-w-full overflow-hidden rounded-none md:aspect-[5/4] lg:aspect-[16/9]">
                  <Image
                    width={1000}
                    height={1000}
                    src={urlFor(item.image ?? item.gallery[0]).url()}
                    alt={
                      item.image?.alt ?? item.gallery[0].alt ?? "Service Image"
                    }
                    className="h-full object-cover blur-sm brightness-50 grayscale-[50%] filter-none group-hover:filter"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 h-full bg-[linear-gradient(hsl(var(--primary)/0),hsl(var(--primary)/0.4),hsl(var(--primary)/0.8)_100%)] mix-blend-multiply" />
                  <div className="absolute inset-x-0 bottom-0 flex flex-col items-start p-6 text-primary-foreground md:p-8">
                    <div className="mb-2 pt-4 text-xl font-semibold md:mb-3 md:pt-4 lg:pt-4">
                      {item.title}
                    </div>
                    <div className="mb-8 line-clamp-2 md:mb-12 lg:mb-9">
                      {item.description}
                    </div>
                    <div className="flex items-center text-sm">
                      Read more{" "}
                      <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="mx-auto my-10 flex w-full max-w-7xl shrink-0 items-center justify-end gap-2">
          <CarouselActionButton
            direction="left"
            onClick={() => carouselAPI?.scrollPrev()}
            disabled={current === 0}
          />
          <CarouselActionButton
            direction="right"
            onClick={() => carouselAPI?.scrollNext()}
            disabled={carouselAPI?.canScrollNext() === false}
          />
        </div>
      </div>
    </section>
  );
};

export { Gallery4 };
